export type StakePlan = {
  readonly stakes: readonly number[];
  readonly payouts: readonly number[];
  readonly spent: number;
  readonly capped: boolean;
  readonly bind: number;
  readonly profit: number;
  readonly middleProfit: number | null;
  readonly placeable: boolean;
};

export type StakeInput = {
  readonly shares: readonly number[];
  readonly odds: readonly number[];
  readonly limits: readonly (number | null)[];
  readonly maxStake: number | null;
  readonly stake: number;
  readonly middle: boolean;
};

const EPSILON = 1e-9;
const SEARCH_RADIUS = 3;

function bindingLeg(shares: readonly number[], limits: readonly (number | null)[]): number {
  let bind = -1;
  let tightest = Infinity;
  limits.forEach((limit, index) => {
    if (limit === null || shares[index] <= 0) return;
    const total = limit / shares[index];
    if (total < tightest) {
      tightest = total;
      bind = index;
    }
  });
  return bind;
}

function total(stakes: readonly number[]): number {
  return stakes.reduce((sum, value) => sum + value, 0);
}

function worstCase(stakes: readonly number[], odds: readonly number[]): number {
  return Math.min(...stakes.map((value, index) => value * odds[index])) - total(stakes);
}

function roundings(level: number, odds: readonly number[], anchor: number, size: number): number[][] {
  let out: number[][] = [[]];
  odds.forEach((price, index) => {
    const exact = level / price;
    const options = index === anchor ? [size] : [...new Set([Math.floor(exact + EPSILON), Math.ceil(exact - EPSILON)])].filter((value) => value >= 1);
    out = out.flatMap((stakes) => options.map((value) => [...stakes, value]));
  });
  return out;
}

export type StakeObjective = "lock" | "spend";

function better(objective: StakeObjective, result: number, spent: number, bestResult: number, bestSpent: number): boolean {
  const sameResult = Math.abs(result - bestResult) <= EPSILON;
  if (objective === "spend") return spent > bestSpent || (spent === bestSpent && !sameResult && result > bestResult);
  return (!sameResult && result > bestResult) || (sameResult && spent > bestSpent);
}

function allocate(shares: readonly number[], odds: readonly number[], rooms: readonly number[], target: number, objective: StakeObjective): number[] {
  const budget = Math.floor(target + EPSILON);
  let best = shares.map((share, index) => Math.min(Math.floor(share * target + EPSILON), rooms[index]));
  let bestResult = worstCase(best, odds);
  let bestSpent = total(best);
  shares.forEach((share, anchor) => {
    const base = Math.floor(share * target + EPSILON);
    for (let size = Math.max(1, base - SEARCH_RADIUS); size <= Math.min(base + SEARCH_RADIUS, rooms[anchor]); size += 1) {
      for (const stakes of roundings(size * odds[anchor], odds, anchor, size)) {
        const spent = total(stakes);
        if (spent > budget || stakes.some((value, index) => value > rooms[index])) continue;
        const result = worstCase(stakes, odds);
        if (better(objective, result, spent, bestResult, bestSpent)) {
          best = stakes;
          bestResult = result;
          bestSpent = spent;
        }
      }
    }
  });
  return best;
}

function priced(stakes: number[], odds: readonly number[], middle: boolean, capped: boolean, bind: number): StakePlan {
  const spent = total(stakes);
  const payouts = stakes.map((value, index) => value * odds[index]);
  return {
    stakes,
    payouts,
    spent,
    capped,
    bind,
    profit: Math.min(...payouts) - spent,
    middleProfit: middle && payouts.length === 2 ? payouts[0] + payouts[1] - spent : null,
    placeable: stakes.every((value) => value >= 1),
  };
}

export function planStakes({ shares, odds, limits, maxStake, stake, middle }: StakeInput, objective: StakeObjective = "lock"): StakePlan {
  const legCap = Math.min(...limits.map((limit, index) => (limit !== null && shares[index] > 0 ? Math.max(0, limit) / shares[index] : Infinity)));
  const cap = Math.min(maxStake !== null ? Math.max(0, maxStake) : Infinity, legCap);
  const target = Math.max(0, Math.min(stake, cap));
  const rooms = limits.map((limit) => (limit !== null ? Math.max(0, Math.floor(limit + EPSILON)) : Infinity));
  return priced(allocate(shares, odds, rooms, target, objective), odds, middle, stake > cap, bindingLeg(shares, limits));
}

export type TypedStake = {
  readonly leg: number;
  readonly amount: number;
};

export function planTypedStakes({ odds, limits, middle }: Pick<StakeInput, "odds" | "limits" | "middle">, typed: readonly TypedStake[]): StakePlan {
  const fixed = new Map(typed.map((entry) => [entry.leg, entry.amount]));
  const level = Math.min(...typed.map((entry) => entry.amount * odds[entry.leg]));
  const rooms = limits.map((limit) => (limit !== null ? Math.max(0, Math.floor(limit + EPSILON)) : Infinity));
  const reaches = odds.map((price, index) => (fixed.has(index) ? Infinity : rooms[index] * price));
  const reach = Math.min(level, ...reaches);
  const bind = reach < level - EPSILON ? reaches.indexOf(reach) : -1;
  const over = typed.some(({ leg, amount }) => {
    const limit = limits[leg];
    return limit !== null && amount > limit + EPSILON;
  });
  let candidates: number[][] = [[]];
  odds.forEach((price, index) => {
    const amount = fixed.get(index);
    const exact = reach / price;
    const ceil = Math.ceil(exact - EPSILON);
    const options = amount !== undefined ? [amount] : [...new Set([Math.max(Math.floor(exact + EPSILON), Math.min(1, ceil)), ceil])];
    candidates = candidates.flatMap((stakes) => options.map((value) => [...stakes, value]));
  });
  let stakes = candidates[0];
  for (const candidate of candidates.slice(1)) {
    if (better("lock", worstCase(candidate, odds), total(candidate), worstCase(stakes, odds), total(stakes))) stakes = candidate;
  }
  return priced(stakes, odds, middle, bind !== -1 || over, bind);
}

export function coverShares(odds: readonly number[]): number[] {
  const inverse = odds.map((price) => 1 / price);
  const sum = inverse.reduce((acc, value) => acc + value, 0);
  return inverse.map((value) => value / sum);
}

export function holdOf(odds: readonly number[]): number {
  return Math.max(0, odds.reduce((acc, price) => acc + 1 / price, 0) - 1);
}

export function legEV(odds: number, fair: number | null): number | null {
  return fair === null || fair <= 0 || fair >= 1 ? null : odds * fair - 1;
}

export function evPercentText(ev: number): string {
  return (ev * 100).toFixed(1);
}

export function isPlusEV(ev: number | null): ev is number {
  return ev !== null && Number(evPercentText(ev)) > 0;
}
